import { FocusEventHandler, Fragment, useState } from "react"

import { CellProps, Col } from "components/Rable/types"
import { CurrencyOption, currency_options } from "utils/currencies"
import { css, cx } from "@emotion/css"

import AutoCompleteInput from "components/inputs/AutoComplete"
import { Combobox } from "components/inputs/Combobox"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { Market } from "models/regions"
import get from "lodash/get"
import { queryCache } from "react-query"
import { useAuthenticated } from "providers/A12n"
import { useContextOptions } from "./SubRegion"
import { useI18n } from "providers/I18n"

function SelectCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { row, rowId, name, getValue, getOutput, onUpdate } = props
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [val, setVal] = useState<string>()

  interface Option {
    label: string
    value: string
  }

  const handleChange = (_: any, option: Option | null) => {
    if (option) {
      setVal(option.value.toString())
    } else {
      setVal("")
    }
  }
  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    const output = getOutput ? getOutput(val) : ({ [name]: val } as Partial<T>)
    onUpdate(rowId, output)
  }

  const renderCurrencyOption = (option: CurrencyOption) => (
    <Fragment>
      <span className={styles.options.short}>{option.symbol}</span>
      <span className={styles.options.label}>{option.label}</span>
    </Fragment>
  )

  return (
    <>
      {hasAuthority(["AUTH_EDIT_MARKET"]) ? (
        <div className={styles.buttonContainer}>
          <Combobox
            renderOption={renderCurrencyOption}
            groupBy={(option) =>
              option.popular
                ? t("inputs.currencies.popular")
                : t("inputs.currencies.all")
            }
            name={name as string}
            label={""}
            style={{ margin: "5px 0 5px 0" }}
            value={val ?? initialValue}
            options={currency_options}
            onChange={handleChange}
            onBlur={onBlur}
            size="medium"
            /*  error={!!error} */
            disabled={!hasAuthority(["AUTH_EDIT_MARKET"])}
          />
        </div>
      ) : (
        <div className={styles.disabledText}>{initialValue}</div>
      )}
    </>
  )
}

function AutoCompleteCellAdmin<T extends Record<string, any>>(
  props: CellProps<T>,
) {
  const { row, rowId, name = "M", getValue, getOutput, onUpdate } = props
  const { hasAuthority } = useAuthenticated()
  const { unassigned, isFetchingUnassigned: isFetching } = useContextOptions()
  const initialValue = getValue ? getValue(row) : get(row, name, "")

  const [value, setValue] = useState(initialValue)

  const [nullValue, setNullValue] = useState<boolean>(false)
  const [valueId, setValueId] = useState<string>()
  const [change, setChange] = useState<boolean>(false)

  const onChange = (e: any) => {
    setChange(true)
    if (e === null) {
      setNullValue(true)
      setValue(undefined)
    } else {
      setValue(e)
      setValueId(e.id)
    }
  }
  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    if (nullValue && change) {
      const output = getOutput
        ? getOutput(valueId)
        : ({ ["marketAdminId" as string]: null } as Partial<T>)
      onUpdate(rowId, output)
      queryCache.invalidateQueries("markets")
      queryCache.invalidateQueries("unassignedMembers")
    } else if (!!valueId) {
      const output = getOutput
        ? getOutput(valueId)
        : ({ ["marketAdminId" as string]: valueId } as Partial<T>)
      onUpdate(rowId, output)
      queryCache.invalidateQueries("markets")
      queryCache.invalidateQueries("unassignedMembers")
    }
  }

  const test = css`
    width: 100%;
  `

  return (
    <div className={test}>
      {hasAuthority(["AUTH_MANAGE_USER_GLOBAL"]) ? (
        <AutoCompleteInput<any>
          disabled={!hasAuthority(["AUTH_MANAGE_USER_GLOBAL"])}
          value={value ?? undefined}
          size="medium"
          margin="normal"
          isLoading={isFetching}
          options={isFetching ? [] : unassigned}
          label={""}
          onChange={(e) => onChange(e)}
          onBlur={onBlur}
          style={{ margin: 0 }}
          fetchOnFilter={false}
          getOptionLabel={(option) => option.name && `${option.name} `}
          getOptionSelected={(option, value) =>
            option ?? option.id === value.id
          }
          renderOption={(option) => (
            <Fragment>
              <div>
                <span>{`${option.firstname} ${option.lastname}`}</span>
              </div>
            </Fragment>
          )}
        />
      ) : (
        <div className={styles.disabledText}>
          {initialValue.name ?? undefined}
        </div>
      )}
    </div>
  )
}

function AutoCompleteCellFinance<T extends Record<string, any>>(
  props: CellProps<T>,
) {
  const { row, rowId, name = "M", getValue, getOutput, onUpdate } = props
  const { hasAuthority } = useAuthenticated()
  const { unassigned, isFetchingUnassigned: isFetching } = useContextOptions()
  const initialValue = getValue ? getValue(row) : get(row, name, "")

  const [value, setValue] = useState(initialValue)

  const [nullValue, setNullValue] = useState<boolean>(false)
  const [valueId, setValueId] = useState<string>()
  const [change, setChange] = useState<boolean>(false)

  const onChange = (e: any) => {
    setChange(true)
    if (e === null) {
      setNullValue(true)
      setValue(undefined)
    } else {
      setValue(e)
      setValueId(e.id)
    }
  }
  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    if (nullValue && change) {
      const output = getOutput
        ? getOutput(valueId)
        : ({ ["marketFinanceId" as string]: null } as Partial<T>)
      onUpdate(rowId, output)
      queryCache.invalidateQueries("markets")
      queryCache.invalidateQueries("unassignedMembers")
    } else if (!!valueId) {
      const output = getOutput
        ? getOutput(valueId)
        : ({ ["marketFinanceId" as string]: valueId } as Partial<T>)
      onUpdate(rowId, output)
      queryCache.invalidateQueries("markets")
      queryCache.invalidateQueries("unassignedMembers")
    }
  }

  const test = css`
    width: 100%;
  `
  return (
    <div className={test}>
      {hasAuthority(["AUTH_MANAGE_USER_GLOBAL"]) ? (
        <AutoCompleteInput<any>
          disabled={!hasAuthority(["AUTH_MANAGE_USER_GLOBAL"])}
          value={value ?? undefined}
          size="medium"
          margin="normal"
          isLoading={isFetching}
          options={isFetching ? [] : unassigned}
          label={""}
          onChange={(e) => onChange(e)}
          onBlur={onBlur}
          style={{ margin: "5px 0 5px 0" }}
          fetchOnFilter={false}
          getOptionLabel={(option) => option.name && `${option.name} `}
          getOptionSelected={(option, value) =>
            option ?? option.id === value.id
          }
          renderOption={(option) => (
            <Fragment>
              <div>
                <span>{`${option.firstname} ${option.lastname}`}</span>
              </div>
            </Fragment>
          )}
        />
      ) : (
        <div className={styles.disabledText}>
          {initialValue.name ?? undefined}
        </div>
      )}
    </div>
  )
}

function EditableCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { row, rowId, name, getValue, getOutput, getError, onUpdate } = props
  const error = getError?.(rowId, name) ?? ""
  const { removeErrors } = useContextOptions()

  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [currentValue, setCurrentValue] = useState(initialValue)

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    const value = evt.target.value
    const hasValueDiff = currentValue.toString() !== value
    const output = getOutput
      ? getOutput(value)
      : ({ [name]: value } as Partial<T>)
    hasValueDiff && onUpdate(rowId, output)
    setCurrentValue(value)
    removeErrors()
  }
  const { hasAuthority } = useAuthenticated()
  const placeholder = "Add " + (name as string) + "..."

  return (
    <GhostTextInput
      // error={!!error}
      // errorMessage={error}
      defaultValue={initialValue}
      placeholder={placeholder}
      onBlur={onBlur}
      className={cx(styles.input, styles.ghostInput, {
        [styles.error]: !!error,
      })}
      disabled={!hasAuthority(["AUTH_EDIT_MARKET"])}
    />
  )
}

const styles = {
  input: css`
    font-size: 13px;
    min-width: 150px;
    :hover {
      cursor: pointer;
    }
  `,
  halfInput: css`
    width: 50%;
  `,
  ghostInput: css`
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  ghostSelect: css`
    align-items: center;
    border: none;

    outline: none;

    .MuiSvgIcon-root {
      display: none;
    }

    :not([data-disabled="true"]):hover {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    :focus,
    :focus-within {
      border-color: var(--primary-main, teal);
      .MuiSvgIcon-root {
        display: block;
      }
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    .MuiSelect-outlined.MuiSelect-outlined {
      padding-top: var(--spacing-xs);
      padding-bottom: var(--spacing-xs);
    }
  `,
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  `,
  buttonHover: css`
    &:hover {
      border: 1px solid var(--primary-main);
    }
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    &:hover {
      background-color: transparent;
      border: 1px solid var(--primary-main);
    }
  `,
  hover: css`
    color: var(--primary-main);
    cursor: pointer;
    font-size: 13px;
    margin-left: 4px;
    margin-right: var(--spacing-m);
  `,
  popover: css`
    pointer-events: none;
  `,
  paper: css`
    padding: 1rem;
    display: flex;
    flex-direction: column;
  `,
  popoverList: css`
    font-size: 13px;
  `,
  primary: css`
    color: var(--primary-main);
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  disabledText: css`
    font-size: 13px;
    color: var(--black);
    text-align: center;
  `,
  options: {
    short: css`
      color: var(--black);
      font-size: 0.8125rem;
      min-width: 2.5rem;
    `,
    label: css`
      color: var(--dark-grey);
      font-size: 0.8125rem;
      margin-left: 0.1rem;
    `,
  },
}

export const COLUMNS: Col<Market>[] = [
  {
    id: "Name",
    name: "name",
    cell: EditableCell,
    width: "10%",
    existsInCreate: true,
    required: true,
  },
  {
    id: "Code",
    name: "code",
    cell: EditableCell,
    existsInCreate: true,
    width: "10%",
    required: true,
  },
  {
    id: "Currency",
    name: "currencyCode",
    width: "20%",
    cell: SelectCell,
    existsInCreate: false,
  },
  {
    id: "Market Admin",
    name: "marketAdmin",
    width: "20%",
    cell: AutoCompleteCellAdmin,
    existsInCreate: false,
  },
  {
    id: "Market Finance",
    name: "marketFinance",
    width: "20%",
    cell: AutoCompleteCellFinance,
    existsInCreate: false,
  },
]
